import type { FieldMetadata } from "@/lib/excel-configurator/core";

/** Campos que a expedição oferece ao configurador, por categoria. Devolve sempre uma lista nova. */
export function buildShipmentFields(): FieldMetadata[] {
  return [
    // ========================================
    // VARIANT FIELDS
    // ========================================
    {
      key: "article_name",
      label: "Nome do artigo",
      type: "text",
      description: "Article name",
      required: true,
      example: "Meias Sport Premium",
    },
    {
      key: "article_code",
      label: "Código do artigo",
      type: "text",
      description: "Internal article code",
      example: "ART-001",
    },
    {
      key: "client_reference",
      label: "Referência do cliente",
      type: "text",
      description: "Código de referência do cliente",
      example: "CLI-REF-123",
    },
    {
      key: "client_description",
      label: "Descrição do cliente",
      type: "text",
      description: "Descrição do artigo pelo cliente",
      example: "Meias Desportivas Premium",
    },
    {
      key: "colour",
      label: "Cor",
      type: "text",
      description: "Cor/variante do artigo",
      example: "Azul",
    },
    {
      key: "size",
      label: "Tamanho",
      type: "text",
      description: "Tamanho do artigo",
      example: "M",
    },
    {
      key: "barcode_type",
      label: "Tipo de código de barras",
      type: "text",
      description: "Formato do código de barras (EAN13, CODE128, etc.)",
      example: "EAN13",
    },
    {
      key: "barcode_value",
      label: "Código de barras",
      type: "text",
      description: "Número/valor do código de barras",
      example: "5901234567890",
    },
    {
      key: "quantity",
      label: "Quantidade",
      type: "number",
      description: "Quantidade a expedir",
      required: true,
      example: "100",
    },
    {
      key: "box_number",
      label: "Número da caixa",
      type: "number",
      description: "Número da caixa/cartão",
      example: "1",
    },
    {
      key: "weight",
      label: "Peso",
      type: "number",
      description: "Peso do artigo",
      example: "0.5",
    },
    {
      key: "volume",
      label: "Volume",
      type: "number",
      description: "Volume do artigo",
      example: "0.01",
    },
    {
      key: "lot_number",
      label: "Número de lote",
      type: "text",
      description: "Número de lote/remessa",
      example: "LOT-2026-001",
    },

    // ========================================
    // PACKING LIST FIELDS
    // ========================================
    {
      key: "packing_list_code",
      label: "Código da packing list",
      type: "text",
      description: "Código de referência da packing list",
      example: "PL-2026-001",
    },
    {
      key: "packing_list_total_boxes",
      label: "Total de caixas",
      type: "number",
      description: "Número total de caixas na packing list",
      example: "10",
    },
    {
      key: "packing_list_net_weight",
      label: "Peso líquido",
      type: "number",
      description: "Peso líquido total da packing list",
      example: "250.5",
    },
    {
      key: "packing_list_gross_weight",
      label: "Peso bruto",
      type: "number",
      description: "Peso bruto total da packing list",
      example: "275.0",
    },

    // ========================================
    // SHIPMENT FIELDS
    // ========================================
    {
      key: "shipment_code",
      label: "Código da expedição",
      type: "text",
      description: "Código de referência da expedição",
      example: "EXP-2026-001",
    },
    {
      key: "shipment_date",
      label: "Data de expedição",
      type: "date",
      description: "Data da expedição",
      example: "2026-02-02",
    },
    {
      key: "shipment_tracking_number",
      label: "Número de rastreio",
      type: "text",
      description: "Número de rastreio da expedição",
      example: "TRACK123456",
    },
    {
      key: "shipment_carrier",
      label: "Transportadora",
      type: "text",
      description: "Nome da transportadora",
      example: "DHL",
    },
    {
      key: "shipment_status",
      label: "Estado da expedição",
      type: "text",
      description: "Estado atual da expedição",
      example: "expedido",
    },

    // ========================================
    // ORDER FIELDS
    // ========================================
    {
      key: "order_code",
      label: "Código da encomenda",
      type: "text",
      description: "Código de referência da encomenda original",
      example: "ORD-2026-001",
    },
    {
      key: "order_date",
      label: "Data da encomenda",
      type: "date",
      description: "Data de criação da encomenda",
      example: "2026-01-15",
    },
    {
      key: "order_status",
      label: "Estado da encomenda",
      type: "text",
      description: "Estado atual da encomenda",
      example: "producao",
    },
    {
      key: "order_priority",
      label: "Prioridade da Encomenda",
      type: "text",
      description: "Nível de prioridade da encomenda",
      example: "alta",
    },
    {
      key: "purchase_order_number",
      label: "Nº da Ordem de Compra",
      type: "text",
      description: "Número da ordem de compra do cliente",
      example: "PO-2026-001",
    },
    {
      key: "order_expected_ship_date",
      label: "Data prevista de envio",
      type: "date",
      description: "Data prevista de envio da encomenda",
      example: "2026-03-01",
    },

    // ========================================
    // CLIENT FIELDS
    // ========================================
    {
      key: "client_name",
      label: "Nome do cliente",
      type: "text",
      description: "Nome da empresa cliente",
      example: "ACME Corporation",
    },
    {
      key: "client_code",
      label: "Código do cliente",
      type: "text",
      description: "Código de referência do cliente",
      example: "CLI-001",
    },
    {
      key: "client_email",
      label: "E-mail do Cliente",
      type: "text",
      description: "Endereço de e-mail do cliente",
      example: "contact@acme.com",
    },
    {
      key: "client_phone",
      label: "Telefone do cliente",
      type: "text",
      description: "Número de telefone do cliente",
      example: "+351 123 456 789",
    },
    {
      key: "client_tax_id",
      label: "NIF do Cliente",
      type: "text",
      description: "Número de identificação fiscal do cliente",
      example: "PT123456789",
    },

    // ========================================
    // DELIVERY ADDRESS FIELDS
    // ========================================
    {
      key: "delivery_address_line1",
      label: "Morada de entrega",
      type: "text",
      description: "Endereço de entrega",
      example: "Rua Principal 123",
    },
    {
      key: "delivery_city",
      label: "Cidade de entrega",
      type: "text",
      description: "Cidade de entrega",
      example: "Porto",
    },
    {
      key: "delivery_postal_code",
      label: "Código postal de entrega",
      type: "text",
      description: "Código postal de entrega",
      example: "4000-000",
    },
    {
      key: "delivery_country",
      label: "País de entrega",
      type: "text",
      description: "País de entrega",
      example: "Portugal",
    },
  ];
}
